import { getRouterBasename, ip_back } from "../config";
import { publicShareOrigin } from "./clubJoinUrl";

/**
 * Demo club (branch demo/club-before-pilot): walk-in players, 30-day retention.
 */

const DEMO_COACH_EMAILS = [
  "ricardo.polar@ping4all.lu",
  "ricardo.polar@gmail.com",
];

function isDemoCoachEmail(email) {
  const normalized = String(email || "")
    .trim()
    .toLowerCase();
  return Boolean(normalized) && DEMO_COACH_EMAILS.includes(normalized);
}

/** Default room code on /demo — joins the demo coach's current live session. */
export const DEMO_SESSION_CODE_DEFAULT = "001";

function truthyFlag(v) {
  return v === true || v === "true" || v === 1 || v === "1";
}

function readSignedInUser(user) {
  if (user && typeof user === "object") return user;
  try {
    const raw =
      typeof localStorage !== "undefined" ? localStorage.getItem("user") : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function isDemoClub(brand, club = null, user = null) {
  const signedIn = readSignedInUser(user);
  const clubBits = [
    brand,
    club,
    signedIn,
    signedIn?.club,
    signedIn?.club_details,
  ];
  for (const obj of clubBits) {
    if (truthyFlag(obj?.is_demo)) return true;
    const code = String(obj?.club_code || "")
      .trim()
      .toLowerCase();
    if (code === "demo") return true;
  }
  return false;
}

export function isDemoWalkInUser(user = null) {
  const signedIn = readSignedInUser(user);
  return Boolean(signedIn?.is_demo_walkin);
}

export function normalizeDemoSessionCode(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return null;
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return null;
  const n = Number.parseInt(digits, 10);
  if (!Number.isFinite(n) || n < 0) return null;
  return String(n).padStart(3, "0");
}

/**
 * Static shareable phone join URL (no session id in the link).
 * Example: https://dev.smartttclub.com/demo
 */
export function getDemoJoinPublicUrl(extras = {}) {
  let origin = "";
  if (extras.origin) {
    try {
      const u = new URL(String(extras.origin));
      origin = publicShareOrigin({
        host: u.host,
        protocol: u.protocol,
      });
    } catch {
      origin = "";
    }
  }
  if (!origin && typeof window !== "undefined" && window.location) {
    origin = publicShareOrigin({
      host: window.location.host,
      protocol: window.location.protocol,
    });
  }
  if (!origin) {
    origin = publicShareOrigin({ host: "smartttclub.com" });
  }
  origin = String(origin).replace(/\/$/, "");

  const basename = String(
    extras.basename != null
      ? extras.basename
      : typeof window !== "undefined"
        ? getRouterBasename()
        : ""
  ).replace(/\/$/, "");

  const path = `${basename}/demo`.replace(/\/+/g, "/") || "/demo";
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
}

/** Read optional session code from the demo join page query string. */
export function readDemoJoinTargetFromLocation(search = "") {
  const raw =
    search ||
    (typeof window !== "undefined" ? window.location.search : "") ||
    "";
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  const sessionCode =
    normalizeDemoSessionCode(params.get("code")) ||
    normalizeDemoSessionCode(params.get("session_code")) ||
    DEMO_SESSION_CODE_DEFAULT;
  return {
    session_code: sessionCode,
    // Legacy deep links — ignored when session_code is sent to the API.
    ts_id: params.get("ts") || params.get("ts_id") || null,
    gr_id: params.get("gr") || params.get("gr_id") || null,
  };
}

/** Build query string for GET/POST /api/demo/join. */
export function demoJoinApiQuery({ session_code, ts_id, gr_id } = {}) {
  const params = new URLSearchParams();
  const code =
    normalizeDemoSessionCode(session_code) || DEMO_SESSION_CODE_DEFAULT;
  params.set("session_code", code);
  if (ts_id) params.set("ts_id", String(ts_id));
  if (gr_id) params.set("gr_id", String(gr_id));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** Tell the server which live session code 001 should join (demo coach only). */
export async function pinDemoLiveSession({ ts_id, gr_id } = {}) {
  const tid = ts_id != null ? String(ts_id).trim() : "";
  if (!tid || typeof window === "undefined") return;
  const token = localStorage.getItem("token");
  if (!token) return;
  try {
    await fetch(`${ip_back}api/demo/pin-session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({
        ts_id: tid,
        gr_id: gr_id != null ? String(gr_id) : undefined,
      }),
    });
  } catch {
    /* non-blocking */
  }
}
